/* * Copyright 2017-2017 Amazon.com, Inc. or its affiliates. All Rights
Reserved. * * Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with * the License. A copy of the
License is located at * * http://aws.amazon.com/apache2.0/ * * or in the
"license" file accompanying this file. This file is distributed on an "AS IS"
BASIS, WITHOUT WARRANTIES OR * CONDITIONS OF ANY KIND, either express or
implied. See the License for the specific language governing permissions * and
limitations under the License. */

<template>
  <div class="signup-container">
    <validation-observer ref="obs" v-slot="ObserverProps">
      <div class="logo"><img src="@/assets/logo.png" /></div>
      <div class="form-input">
        <div class="label">メールアドレス</div>
        <ValidationProvider
          rules="required|customEmail|max:128"
          immediate
          v-slot="ProviderProps"
        >
          <input v-model="email" type="text" autofocus />
          <div class="validation-error">
            {{ ProviderProps.errors[0] }}
          </div>
        </ValidationProvider>
      </div>
      <div class="form-input">
        <div class="label">パスワード</div>
        <ValidationProvider
          rules="required|complexPassword"
          immediate
          v-slot="ProviderProps"
        >
          <input
            v-model="password"
            type="password"
            autocomplete="new-password"
          />
          <p
            class="legend"
            :class="{ 'validation-error': ProviderProps.invalid }"
          >
            8文字以上、アルファベット大文字・小文字・数字をそれぞれ1つ以上含む。
          </p>
        </ValidationProvider>
      </div>
      <div class="agreement_style">
        <ValidationProvider
          :rules="{ required: { allowFalse: false } }"
          v-slot="{ errors }"
          immediate
        >
          <input type="checkbox" v-model="agreement" />
          <a
            href="https://www.sglabs.jp/privacy-policy/"
            target="_blank"
            rel="noopener"
          >
            プライバシーポリシー
          </a>
          および
          <a
            href="https://www.sgnet.co.jp/security/about/"
            target="_blank"
            rel="noopener"
          >
            個人情報の取扱いについて
          </a>
          に同意する。
          <div class="validation-error">{{ errors[0] }}</div>
        </ValidationProvider>
      </div>
      <div class="notes">
        <p>※同じ組織のユーザーはあとから追加できます。</p>
      </div>
      <div class="actions" :class="{ disable: sending }">
        <div class="nav">
          <div>
            <a v-on:click="signIn">戻る</a>
          </div>
        </div>
        <div>
          <div class="submit">
            <span
              class="signup-button"
              :class="{ disable: ObserverProps.invalid }"
              v-on:click="signUp"
              ><span class="arrow-right"></span>登録する</span
            >
          </div>
        </div>
      </div>
      <div class="error" v-if="error">
        {{ error }}
      </div>
    </validation-observer>
    <div class="nav-to-confirm-signup" :class="{ disable: sending }">
      <span @click="confirmSignUpEmpty">
        検証コードを入力される方はこちらから
      </span>
    </div>
  </div>
</template>

<script>
import { AmplifyEventBus } from "aws-amplify-vue";
import { mapActions } from "vuex";
import ErrorMessage from "@/mixins/errormessages";

export default {
  name: "SignUp",
  props: ["signUpConfig", "usernameAttributes"],
  mixins: [ErrorMessage],
  data() {
    return {
      error: "",
      logger: {},
      email: "",
      password: "",
      agreement: false,
      sending: false,
    };
  },
  computed: {
    options() {
      return this.signUpConfig || {};
    },
  },
  mounted() {
    this.logger = new this.$Amplify.Logger(this.$options.name);
  },
  methods: {
    ...mapActions(["checkSystemMaintenance"]),
    signUp: async function () {
      let user = {
        username: this.email,
        password: this.password,
        attributes: {
          email: this.email,
          "custom:display_name": this.email.split("@")[0].substring(0,50),
        },
      };

      this.sending = true;
      if (!(await this.checkMaintenace())) {
        this.sending = false;
        return;
      }

      this.$Amplify.Auth.signUp(user)
        .then((data) => {
          this.sending = false;
          this.logger.info("sign up success");
          AmplifyEventBus.$emit("localUser", data.user);
          AmplifyEventBus.$emit("waitingConfirmSignUp", {
            password: this.password,
            afterSignUp: true,
            hasRegisteredEmail: true,
          });
          return AmplifyEventBus.$emit("authState", "confirmSignUp");
        })
        .catch((e) => {
          this.sending = false;
          this.setError(e);
        });
    },

    confirmSignUpEmpty: async function () {
      if (!(await this.checkMaintenace())) {
        return;
      }
      AmplifyEventBus.$emit("waitingConfirmSignUp", {});
      AmplifyEventBus.$emit("authState", "confirmSignUp");
    },

    signIn: function () {
      AmplifyEventBus.$emit("authState", "signIn");
    },

    setError: function (e) {
      this.error = this.convertSignUpErrorMessage(e);
      this.logger.error(this.error);
    },

    checkMaintenace: async function () {
      try {
        this.error = "";
        const res = await this.checkSystemMaintenance();
        if (res && res.data && res.data.code === "9") {
          this.error = res.data.message;
          return false;
        }
      } catch (e) {
        console.log(e);
        this.error = "サーバーへの接続に失敗しました。";
        return false;
      }
      return true;
    },
  },
};
</script>

<style lang="scss">
.signup-container {
  width: 100vw;
  background: #ffffff;
  padding: 3rem;
  color: var(--purple);

  .desktop & {
    width: 552px;
    box-shadow: 0px 0px 20px #00000029;
    border-radius: 25px;
  }

  .logo {
    height: 3rem;
    margin-bottom: 2rem;
    display: flex;
    justify-content: center;

    img {
      object-fit: contain;
    }
  }

  .form-input {
    margin-bottom: 1rem;

    .label {
      margin-bottom: 0.5rem;
    }

    input[type="text"],
    input[type="password"],
    input[type="date"] {
      width: 100%;
      height: 40px;
      border-radius: 20px;
      border: solid 1px var(--purple);
      padding: 0 16px;
      font-size: 16px;
      outline: none;
    }

    input[type="text"]:focus,
    input[type="password"]:focus,
    input[type="date"]:focus {
      background: var(--purple-tertiary);
    }
  }

  .notes {
    margin-top: 2rem;
    font-size: 0.8rem;
  }
  .nav-to-confirm-signup {
    margin-top: 4rem;
    font-size: 0.8rem;
    text-align: right;
    &.disable {
      cursor: none;
      pointer-events: none;
      opacity: 0.5;
    }
  }

  .agreement_style {
    font-size: 0.9rem;
    padding-top: 20px;
  }

  .actions {
    margin-top: 3rem;
    display: flex;
    align-items: center;

    .desktop & {
      margin-top: 2rem;
      justify-content: space-between;
    }

    &.disable {
      cursor: none;
      pointer-events: none;
      opacity: 0.5;
    }

    .nav {
      display: flex;
      justify-content: start;
      align-items: center;
      color: #1a82b7;
      cursor: pointer;

      .mobile & {
        margin-top: 3rem;
      }

      a:active {
        opacity: 0.5;
      }
    }

    .submit {
      .signup-button {
        display: flex;
        justify-content: center;
        align-items: center;
        background: var(--purple);
        width: 140px;
        height: 44px;
        border-radius: 22px;
        color: #fff;
        cursor: pointer;

        &:active {
          opacity: 0.7;
        }
        &.disable {
          cursor: none;
          pointer-events: none;
          opacity: 0.5;
        }
      }
    }

    .nav-signup {
      padding-top: 1rem;
      justify-content: center;
    }
  }
}
</style>
